import { useAuth0 } from "@auth0/auth0-react";
import React from "react";
import {
  useAllStocksQuery,
  useCurrentUserStocksQuery,
  useFollowStockMutation,
} from "../generated/graphql";
import StockItem from "./StockItem";
import ErrorMessage from "./ErrorMessage";

const Stocks: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth0();

  const { loading, data, error, refetch } = useCurrentUserStocksQuery({
    skip: !isAuthenticated,
  });

  const { data: allStocksData } = useAllStocksQuery({
    skip: !isAuthenticated,
  });

  const [followStock, { loading: followLoading }] = useFollowStockMutation({
    refetchQueries: ['currentUserStocks', 'allStocks'],
  });

  if (loading || isLoading) {
    return <div className="text-center text-gray-500">Chargement...</div>;
  }

  if (!isAuthenticated) {
    return (
      <div className="text-center text-red-500">
        Veuillez vous connecter pour accéder à la section Stocks
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto">
        <h1 className="text-2xl font-bold mb-6">Gestion des Stocks</h1>
        <ErrorMessage
          error={error}
          onRetry={() => refetch()}
          title="Erreur lors du chargement des stocks"
          className="mb-6"
        />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="container mx-auto">
        <h1 className="text-2xl font-bold mb-6">Gestion des Stocks</h1>
        <ErrorMessage
          onRetry={() => refetch()}
          title="Erreur lors du chargement des stock - pas de données"
          className="mb-6"
        />
      </div>
    );
  }

  const followedIds = new Set(data.currentUser.stocks.map((s) => s.stock.id));
  const unfollowedStocks = (allStocksData?.stocks ?? []).filter(
    (s) => !followedIds.has(s.id),
  );

  return (
    <div className="container mx-auto">
      <h1 className="text-2xl font-bold mb-6">Gestion des Stocks</h1>

      <div className="bg-white rounded-lg shadow-md">
        {data.currentUser.stocks.length > 0 ? (
          <div className="mt-6 overflow-x-auto">
            <table className="min-w-full bg-white">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nom de l'action
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recommandation
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dernier signal
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dernier calcul
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cours
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Gain
                  </th>
                  <th className="px-6 py-3" />
                </tr>
              </thead>
              <tbody>
                {data.currentUser.stocks.map((stock) => (
                  <StockItem key={stock.id} stock={stock} />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="mt-6 p-4 bg-gray-50 rounded-sm border border-gray-200">
            <p className="text-gray-500 italic">
              Aucun stock disponible pour le moment.
            </p>
          </div>
        )}
      </div>

      {unfollowedStocks.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-semibold mb-3 text-gray-700">
            Découvrir des actions
          </h2>
          <div className="bg-white rounded-lg shadow-md divide-y">
            {unfollowedStocks.map((stock) => (
              <div
                key={stock.id}
                className="flex items-center justify-between px-6 py-3"
                data-testid={`stock-discover-row-${stock.id}`}
              >
                <div>
                  <span className="font-medium">{stock.name}</span>
                  <span className="ml-2 text-xs text-gray-400">{stock.symbol}</span>
                </div>
                <button
                  onClick={() => followStock({ variables: { stockId: stock.id } })}
                  disabled={followLoading}
                  data-testid={`stock-follow-btn-${stock.id}`}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50 focus:outline-hidden focus:ring-2 focus:ring-blue-500 transition-colors"
                >
                  + Suivre
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Stocks;
